import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Headphones, BookMarked, User, Milestone } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { api } from "@/lib/api";
import { Loader, PageHeader, RetellingBadge } from "@/components/cast/Common";

export default function Boat() {
  const [series, setSeries] = useState<any[] | null>(null);
  const [filter, setFilter] = useState("all");
  useEffect(() => { api.get("/series").then((r) => setSeries(r.data)); }, []);
  if (!series) return <Loader />;
  const FILTERS = [["all", "Genesis to Revelation"], ["old", "Old Testament"], ["new", "New Testament"]];
  const shown = filter === "all" ? series : series.filter((s) => s.testament === filter);

  return (
    <div>
      <PageHeader metaphor="Boat" label="Story Journeys" title="Step into the story." subtitle="Illustrated, narrated retellings in episodes. Each one is labeled as a retelling and points you back to the Scripture it comes from." />
      <div className="mb-8 flex flex-wrap gap-2" data-testid="testament-filter">
        {FILTERS.map(([c, l]) => (
          <button key={c} data-testid={`testament-filter-${c}`} onClick={() => setFilter(c)}
            className={`rounded-full border px-4 py-1.5 text-sm font-semibold transition-colors ${filter === c ? "border-[#1E293B] bg-[#1E293B] text-white" : "border-[#E2DBCE] bg-white/90 text-slate-700 hover:bg-[#F3ECE0]"}`}>{l}</button>
        ))}
        <span className="self-center pl-2 text-sm text-slate-500" data-testid="series-count">{shown.length} journeys · {shown.reduce((n, s) => n + s.episodes.length, 0)} stories</span>
        <Link to="/timeline" data-testid="open-timeline-link" className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-[#C86D51] px-4 py-1.5 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5"><Milestone className="h-4 w-4" />Bible timeline</Link>
      </div>
      <div className="grid gap-8 md:grid-cols-2">
        {shown.map((s, i) => (
          <motion.div key={s.id} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}>
            <Link to={`/boat/${s.id}`} data-testid={`series-card-${s.id}`} className="group block overflow-hidden rounded-3xl border border-[#E2DBCE] bg-white transition-all hover:-translate-y-1 hover:shadow-xl">
              <div className="relative overflow-hidden">
                <img src={s.cover} alt={s.title} className="aspect-[16/9] w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <span className="absolute right-4 top-4 flex items-center gap-1 rounded-full bg-white/90 px-3 py-1 text-xs font-semibold text-slate-800 backdrop-blur"><Headphones className="h-3.5 w-3.5" />Narrated</span>
              </div>
              <div className="space-y-3 p-7">
                <RetellingBadge />
                <h2 className="font-display text-3xl font-semibold text-slate-900">{s.title}</h2>
                <p className="text-slate-600">{s.summary}</p>
                <div className="flex flex-wrap gap-4 text-sm text-slate-500">
                  <span className="flex items-center gap-1"><User className="h-4 w-4" />{s.character}</span>
                  <span className="flex items-center gap-1"><BookMarked className="h-4 w-4" />{s.book}</span>
                </div>
                <div className="pt-2">
                  <Progress value={(s.completed_count / s.episodes.length) * 100} className="h-1.5" />
                  <p className="mt-2 text-xs text-slate-500" data-testid={`series-progress-${s.id}`}>{s.completed_count} of {s.episodes.length} episodes complete</p>
                </div>
              </div>
            </Link>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
